import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import {
  Box,
  Button,
  Collapse,
  Dialog,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import costIcon from "@iconify/icons-mdi/cash";
import projectIcon from "@iconify/icons-mdi/chart-gantt";
import timerOutlineIcon from "@iconify/icons-mdi/timer-outline";
import clientIcon from "@iconify/icons-mdi/user-tie";
import { InlineIcon } from "@iconify/react";
import {
  MyTimesheetClientSelector,
  MyTimesheetCostSelector,
  MyTimesheetNewRequestDialogDuration,
  MyTimesheetNewRequestDialogTimeDuration,
  MyTimesheetProjectSelector,
  MyTimesheetShowRequestDialogHeader,
} from "my-timesheet/components";
import { checkOverlappingEvents } from "my-timesheet/utils";

type MyTimesheetRequestDialogProps = {
  requestData: {
    _id: string;
    name?: string;
    description?: string;
    projectId?: string;
    clientId?: string;
    costId?: string;
    startTime: string | Date;
    endTime: string | Date;
    validFrom?: string | Date;
    validTo?: string | Date;
    projectName?: string;
    clientName?: string;
    costName?: string;
  } | null;
  openDialog: boolean;
  onClose: () => void;
  updateMyRequest: (args: any) => Promise<any>;
  setStartRecording: (val: boolean) => void;
  setShowValues: (val: boolean) => void;
  formik: any;
  events: any[];
  isMobile: boolean;
};

export const MyTimesheetRequestDialog = ({
  requestData,
  openDialog,
  onClose,
  updateMyRequest,
  setStartRecording,
  setShowValues,
  formik,
  events,
  isMobile,
}: MyTimesheetRequestDialogProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [openEditRequest, setOpenEditRequest] = useState(false);
  const [initialValuesSet, setInitialValuesSet] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    if (requestData && !initialValuesSet && !openEditRequest) {
      formik.setValues({
        name: requestData?.name || "",
        description: requestData?.description || "",
        projectId: requestData?.projectId || "",
        clientId: requestData?.clientId || "",
        costId: requestData?.costId || "",
        startTime: requestData?.startTime
          ? new Date(requestData.startTime)
          : "",
        endTime: requestData?.endTime ? new Date(requestData.endTime) : "",
      });
      setInitialValuesSet(true);
    }
  }, [requestData, initialValuesSet, openEditRequest, formik]);

  if (!requestData) {
    return null;
  }

  const startDate = new Date(requestData?.startTime).toLocaleDateString();
  const endDate = new Date(requestData?.endTime).toLocaleDateString();
  const startTime = moment(requestData?.startTime).format("HH:mm");
  const endTime = moment(requestData?.endTime).format("HH:mm");
  const duration = moment
    .utc(
      new Date(requestData.endTime).getTime() -
      new Date(requestData.startTime).getTime(),
    )
    .format("HH:mm");

  const formikDuration = moment
    .utc(
      new Date(formik.values.endTime).getTime() -
      new Date(formik.values.startTime).getTime(),
    )
    .format("HH:mm");

  const handleSendApproval = async () => {
    if (isSubmitting) return;

    const today = moment().endOf("day");

    if (!formik.values.startTime || !formik.values.endTime) {
      setErrorMessage(t("START_AND_END_DATE_REQUIRED"));
      return;
    }

    if (
      moment(formik.values.endTime).isBefore(moment(formik.values.startTime))
    ) {
      setErrorMessage(t("OC_END_DATE_BEFORE_START_DATE"));
      return;
    }

    if (moment(formik.values.startTime).isAfter(today)) {
      setErrorMessage(t("OC_DATE_IN_FUTURE"));
      return;
    }

    const dayDifference = moment(formik.values.endTime).diff(
      moment(formik.values.startTime),
      "days",
    );

    if (dayDifference > 1) {
      setErrorMessage(t("OC_DATE_TOO_FAR_AWAY"));
      return;
    }

    if (!formik.values.name) {
      setErrorMessage(t("OC_NAME_REQUIRED"));
      return;
    }

    const hasOverlap = checkOverlappingEvents(events, formik.values);
    if (hasOverlap) {
      setErrorMessage(t("OC_OVERLAPPING_TIME_PERIODS"));
      return;
    }

    const twoDaysAgo = moment().startOf("day").subtract(2, "days");
    if (
      moment(formik.values.startTime).isBefore(twoDaysAgo) ||
      moment(formik.values.endTime).isBefore(twoDaysAgo)
    ) {
      setErrorMessage(t("OC_DATE_IN_FUTURE"));
      return;
    }

    try {
      setErrorMessage(null);
      setIsSubmitting(true);
      await updateMyRequest({
        variables: {
          _id: requestData._id,
          name: formik.values.name,
          description: formik.values.description,
          startTime: formik.values.startTime,
          endTime: formik.values.endTime,
          projectId: formik.values.projectId,
          clientId: formik.values.clientId,
          costId: formik.values.costId,
          validFrom: requestData?.validFrom,
          validTo: requestData?.validTo,
        },
      });
      setStartRecording(false);
      setShowValues(false);
      localStorage.removeItem("showValues");
      formik.resetForm();
      onClose();
    } catch (error) {
      console.error("Failed to update request:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenEditRequest = () => {
    setOpenEditRequest(true);
  };

  const handleCloseEditRequest = () => {
    setOpenEditRequest(false);
    setErrorMessage(null);
    formik.setFieldValue("startTime", requestData?.startTime);
    formik.setFieldValue("endTime", requestData?.endTime);
  };

  const handleCloseAllDialog = () => {
    setOpenEditRequest(false);
    setErrorMessage(null);
    formik.setFieldValue("startTime", requestData?.startTime);
    formik.setFieldValue("endTime", requestData?.endTime);
    onClose();
  };

  let disabled = false;

  const durationDisabled = openEditRequest ? formikDuration : duration;

  if (durationDisabled === "00:00" || isSubmitting) {
    disabled = true;
  }

  return (
    <Dialog open={openDialog} fullScreen={isMobile ? true : false}>
      <Box id="my-sheet-request-dialog" padding="0px 16px 16px 16px">
        <MyTimesheetShowRequestDialogHeader
          label={t("MY_TIMESHEET_NEW_ACTIVITY")}
          close={onClose}
          isMobile={isMobile}
        />
        <Box
          style={{
            height: "2px",
            backgroundColor: "#C6CACC",
            marginBottom: "16px",
          }}
        />
        <Box display="flex" flexDirection="row">
          <Box
            width="100%"
            style={{
              padding: "8px 16px",
              backgroundColor: "#E3E5E5",
              borderRadius: "4px",
            }}
          >
            <Box display="flex" flexDirection="row">
              <InlineIcon
                icon={timerOutlineIcon}
                width="24px"
                style={{
                  marginRight: "8px",
                  color: "#cc0033",
                }}
              />
              <Typography variant="body1" color="primary">
                {requestData?.name}
              </Typography>
            </Box>
            {requestData?.projectName && (
              <Box display="flex" flexDirection="row">
                <InlineIcon
                  icon={projectIcon}
                  width="24px"
                  style={{
                    marginRight: "8px",
                  }}
                />
                <Typography variant="body1">
                  {requestData?.projectName}
                </Typography>
              </Box>
            )}
            {requestData?.clientName && (
              <Box display="flex" flexDirection="row">
                <InlineIcon
                  icon={clientIcon}
                  width="24px"
                  style={{
                    marginRight: "8px",
                  }}
                />
                <Typography variant="body1">
                  {requestData?.clientName}
                </Typography>
              </Box>
            )}
            {requestData?.costName && (
              <Box display="flex" flexDirection="row">
                <InlineIcon
                  icon={costIcon}
                  width="24px"
                  style={{
                    marginRight: "8px",
                  }}
                />
                <Typography variant="body1">{requestData?.costName}</Typography>
              </Box>
            )}
          </Box>
        </Box>
        <Box height="32px" />
        <Box
          display="flex"
          flexDirection={isMobile ? "column" : "row"}
          justifyContent="space-between"
        >
          <Box width={isMobile ? "auto" : "352px"}>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              height="60px"
              padding="16px"
              style={{
                backgroundColor: "#E3E5E5",
                borderRadius: "4px",
              }}
            >
              <Typography
                variant="body1"
                style={{
                  color: "#4D4D4D",
                }}
              >
                {t("OC_START_TIME")}
              </Typography>
              <Box
                display="flex"
                flexDirection="row"
                width="320px"
                style={{
                  borderTop: "1px solid #C6CACC",
                  paddingTop: "8px",
                  marginTop: "4px",
                }}
              >
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  width="50%"
                >
                  <Typography variant="h6">{startDate}</Typography>
                </Box>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  width="50%"
                >
                  <Typography variant="h6">{startTime}</Typography>
                </Box>
              </Box>
            </Box>
            <Box height="16px" />
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              height="60px"
              padding="16px"
              style={{
                backgroundColor: "#E3E5E5",
                borderRadius: "4px",
              }}
            >
              <Typography
                variant="body1"
                style={{
                  color: "#4D4D4D",
                }}
              >
                {t("OC_END_TIME")}
              </Typography>
              <Box
                display="flex"
                flexDirection="row"
                width="320px"
                style={{
                  borderTop: "1px solid #C6CACC",
                  paddingTop: "8px",
                  marginTop: "4px",
                }}
              >
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  width="50%"
                >
                  <Typography variant="h6">{endDate}</Typography>
                </Box>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  width="50%"
                >
                  <Typography variant="h6">{endTime}</Typography>
                </Box>
              </Box>
            </Box>
          </Box>
          {isMobile && <Box height="16px" />}
          <Box width="16px" />
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            width="168px"
            height="184px"
            padding="16px 16px 0px 16px"
            style={{
              backgroundColor: "#E3E5E5",
              borderRadius: "4px",
            }}
          >
            <Typography
              variant="h2"
              color="#CC0033"
              style={{ fontWeight: "400" }}
            >
              {duration}
            </Typography>
            <Box height="36px" />
            <Typography
              variant="body1"
              style={{
                color: "#4D4D4D",
              }}
            >
              {t("MY_TIME_WORKING_TIME")}
            </Typography>
          </Box>
        </Box>
        <Box height="32px" />
        <TextField
          id="outlined-multiline-static"
          label={t("OC_NOTE")}
          name="description"
          multiline
          minRows={3}
          variant="outlined"
          disabled={true}
          style={{ width: isMobile ? "100%" : "568px", height: "auto" }}
          value={requestData?.description || ""}
          helperText={
            !requestData?.description
              ? t("OC_OPTIONAL")
              : `${requestData?.description?.length}/255`
          }
          FormHelperTextProps={
            !requestData?.description
              ? { style: { textAlign: "left" } }
              : { style: { textAlign: "right" } }
          }
          InputLabelProps={{ shrink: true }}
        />
        <Box height="16px" />
        <Box
          style={{
            height: "1px",
            backgroundColor: "#C6CACC",
            marginBottom: "16px",
          }}
        />
        <Box
          display="flex"
          flexDirection={isMobile ? "column-reverse" : "row"}
          justifyContent="space-between"
          width={isMobile ? "auto" : "568px"}
        >
          <Button
            id="edit-request"
            onClick={handleOpenEditRequest}
            variant="contained"
            style={{
              width: isMobile ? "100%" : "calc(50% - 8px)",
              boxShadow: "unset",
              backgroundColor: "#E3E5E5",
              color: "#212121",
            }}
          >
            {t("MY_TIMESHEET_EDIT_ACTIVITY")}
          </Button>
          {isMobile && <Box height="16px" />}
          <Button
            id="send-approval"
            onClick={handleSendApproval}
            variant="contained"
            color="primary"
            disableElevation
            disabled={disabled}
            style={{
              width: isMobile ? "100%" : "calc(50% - 8px)",
            }}
          >
            {t("MY_TIMESHEET_SEND_APPROVAL")}
          </Button>
        </Box>
        <Dialog
          open={openEditRequest}
          onClose={handleCloseEditRequest}
          fullScreen={isMobile ? true : false}
        >
          <Box padding="0px 16px 16px 16px">
            <MyTimesheetShowRequestDialogHeader
              label={t("MY_TIMESHEET_EDIT_ACTIVITY")}
              close={handleCloseAllDialog}
              isMobile={isMobile}
            />
            <Box
              style={{
                height: "2px",
                backgroundColor: "#C6CACC",
                marginBottom: "16px",
              }}
            />
            <Box height="18px" />
            <TextField
              id="name"
              name="name"
              label={t("OC_NAME")}
              variant="outlined"
              value={formik.values.name}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={(formik.touched.name && t(formik.errors.name)) || " "}
              onChange={formik.handleChange}
              style={{ width: isMobile ? "100%" : "568px" }}
            />
            <Box height="16px" />
            <MyTimesheetProjectSelector formik={formik} />
            <MyTimesheetClientSelector formik={formik} />
            <MyTimesheetCostSelector formik={formik} />
            <Box height="16px" />
            <Box
              display="flex"
              flexDirection={isMobile ? "column" : "row"}
              justifyContent="space-between"
              width={isMobile ? "auto" : "568px"}
            >
              <MyTimesheetNewRequestDialogTimeDuration
                formik={formik}
                isMobile={isMobile}
              />
              {isMobile && <Box height="16px" />}
              <MyTimesheetNewRequestDialogDuration formik={formik} />
            </Box>
            <Box width={isMobile ? "auto" : "568px"}>
              <Box height="32px" />
              <TextField
                id="outlined-multiline-static"
                label="Megjegyzés"
                name="description"
                multiline
                minRows={3}
                variant="outlined"
                fullWidth
                style={{ height: "auto" }}
                value={formik.values.description || ""}
                error={
                  formik.touched.description &&
                  Boolean(formik.errors.description)
                }
                helperText={
                  !formik.values.description
                    ? t("OC_OPTIONAL")
                    : `${formik.values.description?.length}/255`
                }
                FormHelperTextProps={
                  !formik.values.description
                    ? { style: { textAlign: "left" } }
                    : { style: { textAlign: "right" } }
                }
                onChange={formik.handleChange}
              />
            </Box>
            <Box height="16px" />
            <Box
              style={{
                height: "1px",
                backgroundColor: "#C6CACC",
                marginBottom: "16px",
              }}
            />
            <Collapse in={!!errorMessage}>
              {errorMessage && (
                <Box marginBottom="16px">
                  <Typography
                    variant="body2"
                    color={theme.palette.primary.main}
                  >
                    {errorMessage}
                  </Typography>
                </Box>
              )}
            </Collapse>
            <Box
              display="flex"
              flexDirection={isMobile ? "column-reverse" : "row"}
              justifyContent="space-between"
            >
              <Button
                id="cancel-send-approval"
                onClick={handleCloseAllDialog}
                style={{ width: isMobile ? "100%" : "calc(50% - 8px)" }}
                variant="text"
                color="secondary"
              >
                {t("OC_CANCEL")}
              </Button>
              {isMobile && <Box height="16px" />}
              <Button
                id="send-approval"
                onClick={handleSendApproval}
                variant="contained"
                color="primary"
                disableElevation
                disabled={disabled}
                style={{
                  width: isMobile ? "100%" : "calc(50% - 8px)",
                }}
              >
                {t("MY_TIMESHEET_SEND_APPROVAL")}
              </Button>
            </Box>
          </Box>
        </Dialog>
      </Box>
    </Dialog>
  );
};

export default MyTimesheetRequestDialog;
